import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface BurstState {
  show: boolean;
  kind: 'activate' | 'deactivate';
  origin: { x: number; y: number };
}

interface SidecarModeContextType {
  sidecarMode: boolean;
  isAnimating: boolean;
  isExiting: boolean;
  burst: BurstState;
  toggleSidecarMode: (originElement?: HTMLElement | null) => void;
}

const SidecarModeContext = createContext<SidecarModeContextType | undefined>(undefined);

// Routes that should be remembered when exiting sidecar mode
const REMEMBERABLE_ROUTES = [
  '/chemistry/projects',
  '/chemistry/files',
  '/chemistry/file',
  '/omics',
  '/knowledge',
  '/molecule',
  '/protein',
];

export const SidecarModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidecarMode, setSidecarMode] = useState(() => {
    try {
      const stored = localStorage.getItem('discoverant-sidecar-mode');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [burst, setBurst] = useState<BurstState>({
    show: false,
    kind: 'activate',
    origin: { x: 0, y: 0 }
  });

  // Store the previous location before entering sidecar mode
  const previousLocationRef = useRef<string>('/chemistry/projects');

  // Timer refs for cleanup
  const burstTimer = useRef<number | null>(null);
  const animTimer = useRef<number | null>(null);
  const navTimer = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (burstTimer.current) clearTimeout(burstTimer.current);
      if (animTimer.current) clearTimeout(animTimer.current);
      if (navTimer.current) clearTimeout(navTimer.current);
    };
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('discoverant-sidecar-mode', String(sidecarMode));
    } catch {
      // Ignore persistence failures; mode can still live in memory.
    }
  }, [sidecarMode]);

  // Track current location for "return to previous" functionality
  useEffect(() => {
    if (!sidecarMode) {
      // Only remember routes that make sense to return to
      const isRememberable = REMEMBERABLE_ROUTES.some(route =>
        location.pathname.startsWith(route)
      );
      if (isRememberable) {
        previousLocationRef.current = location.pathname + location.search;
      }
    }
  }, [location, sidecarMode]);

  const toggleSidecarMode = useCallback((originElement?: HTMLElement | null) => {
    // Prevent rapid toggling
    if (isAnimating || isExiting) return;

    // Compute next mode BEFORE setting state
    const nextMode = !sidecarMode;

    // Get burst origin
    let origin = { x: window.innerWidth / 2, y: 30 };
    if (originElement) {
      const rect = originElement.getBoundingClientRect();
      origin = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }

    // Clear previous timers
    if (burstTimer.current) clearTimeout(burstTimer.current);
    if (animTimer.current) clearTimeout(animTimer.current);
    if (navTimer.current) clearTimeout(navTimer.current);

    if (nextMode) {
      // ENTERING External Mode - navigate immediately, animate in
      setBurst({
        show: true,
        kind: 'activate',
        origin
      });

      setIsAnimating(true);
      setSidecarMode(true);

      // Navigate immediately for entering
      navTimer.current = window.setTimeout(() => {
        navigate('/chemistry');
      }, 100);

      // Burst and animation timers
      burstTimer.current = window.setTimeout(() => {
        setBurst(prev => ({ ...prev, show: false }));
      }, 1200);

      animTimer.current = window.setTimeout(() => {
        setIsAnimating(false);
      }, 800);
    } else {
      // EXITING External Mode - animate out FIRST, then navigate
      setIsExiting(true);
      setIsAnimating(true);

      // After exit animation completes, navigate and change mode
      navTimer.current = window.setTimeout(() => {
        // Set burst for deactivate effect
        setBurst({
          show: true,
          kind: 'deactivate',
          origin
        });

        setIsExiting(false);
        setSidecarMode(false);
        navigate(previousLocationRef.current);

        // Burst timer
        burstTimer.current = window.setTimeout(() => {
          setBurst(prev => ({ ...prev, show: false }));
        }, 1000);

        // End animation state
        animTimer.current = window.setTimeout(() => {
          setIsAnimating(false);
        }, 600);
      }, 400); // Wait for exit animation
    }
  }, [sidecarMode, isAnimating, isExiting, navigate]);

  return (
    <SidecarModeContext.Provider value={{ sidecarMode, isAnimating, isExiting, burst, toggleSidecarMode }}>
      {children}
    </SidecarModeContext.Provider>
  );
};

export const useSidecarMode = () => {
  const context = useContext(SidecarModeContext);
  if (!context) {
    throw new Error('useSidecarMode must be used within SidecarModeProvider');
  }
  return context;
};
